import React, { useState, useEffect } from "react";
import { Add, Remove, Close } from "@material-ui/icons";
import { FiShoppingCart, FiPlus, FiMinus, FiEdit2 } from "react-icons/fi";
import Select from "react-select";
import { commonSelectProps } from "../../../components/shared/SelectStyles";
import { PAYMENT_TYPE_OPTIONS } from "../../../constants/paymentTypeOptions";
import { ORDER_FORM_PAYMENTS } from "../../../constants/paymentSlots";
import IncludedServicesField from "../../../components/shared/IncludedServicesField";
import {
    TOTAL_COST_BREAKDOWN_FIELDS,
    getTotalCostBreakdownSum,
} from "../../../utils/totalCostBreakdown";

const getCartLineItemCode = (line) =>
    line.color?.itemCode || line.activeColor?.itemCode || line.itemCode;

const isOthersCartLine = (line) => {
    const ic = getCartLineItemCode(line);
    return ic === "OTHERS" || ic === "others";
};

const isGenericOthersLabel = (s) =>
    !s || s === "OTHERS" || s === "Others" || s === "others";

// The main Awning catalog product ID - only this product should show generic "Awning" type labels
const MAIN_AWNING_CATALOG_PRODUCT_ID = "69832bf1412f46e3c0c8dec7";

const awningFirst = (a, b) =>
    (b.title === "Awning") - (a.title === "Awning");

// Get the productId as a string (handles both string and populated object)
const getLineProductId = (line) => {
    const id = line._id;
    if (!id) return null;
    if (typeof id === 'string') return id;
    if (typeof id === 'object' && id._id) return String(id._id);
    if (typeof id === 'object' && id.id) return String(id.id);
    return String(id);
};

/** 
 * Determines the label to show in the TYPE column.
 * - For the main Awning catalog product (69832bf1412f46e3c0c8dec7): show "Awning" or type description
 * - For ALL other products: show the code (stored in color.name) - this is the products.code value
 */
const cartLineTypeLabel = (line) => {
    const productId = getLineProductId(line);
    const isMainAwningProduct = productId === MAIN_AWNING_CATALOG_PRODUCT_ID;
    const c = line.color || line.activeColor || {};
    const customTitle = String(c.ipohProductName || c.customTitle || "").trim();
    if (customTitle) return customTitle;
    
    // For products that are NOT the main Awning catalog product, show the code (color.name)
    if (!isMainAwningProduct) {
        const fromName = c.name != null ? String(c.name).trim() : "";
        const fromType = c.type != null ? String(c.type).trim() : "";
        
        // Prefer name (which holds products.code), then type, then fallbacks
        // Only skip if it's a generic "Others" label AND we have a better alternative
        if (fromName && !isGenericOthersLabel(fromName)) return fromName;
        if (fromType && !isGenericOthersLabel(fromType)) return fromType;
        // If both are generic or empty, still try to show name if it exists
        if (fromName) return fromName;
        if (fromType) return fromType;
        // Final fallback
        return line.title || line.type || "N/A";
    }
    
    // For the main Awning catalog product, use type/title (show "Awning" type label)
    if (isOthersCartLine(line)) {
        const fromType = c.type != null ? String(c.type).trim() : "";
        const fromName = c.name != null ? String(c.name).trim() : "";
        if (!isGenericOthersLabel(fromType)) return fromType;
        if (!isGenericOthersLabel(fromName)) return fromName;
    }
    return line.type || line.activeColor?.type || line.title || "N/A";
};

const ProductsTab = ({
    cart,
    total,
    inputs,
    handleChange,
    handleQuantity,
    handlePosCreateOrder,
    cashierMode,
    setCashierMode,
    setFilterList,
    products,
    setColor,
    setProduct,
    setItemDetails,
    setColorPicker,
    NumberFormatter,
    superadmin,
    manager,
    staff,
    outright,
    expenses,
    handleExpensesChange,
    orderData,
    pendingMode,
    setPendingMode,
    sstIncluded,
    setSstIncluded,
    onEditCartProduct,
    hideExpenses,
    useTotalCostBreakdown = false,
}) => {
    const [expensesOpen, setExpensesOpen] = useState(true);
    const [productInfoOpen, setProductInfoOpen] = useState(true);
    const [customerDetailsOpen, setCustomerDetailsOpen] = useState(true);
    const [selectProductsModalOpen, setSelectProductsModalOpen] = useState(false);
    const [isMobile, setIsMobile] = useState(typeof window !== "undefined" && window.innerWidth <= 768);

    const isEditingAllowed = orderData?.status !== "completed";

    const canEditQuantity =
        orderData?.status === "uploadedDraft" ||
        orderData?.status === "purchasing" ||
        (orderData?.status === "pending" && (outright || manager || staff));

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const selectProductsContent = (
        <>
            <div className="checkoutButtons">
                <div
                    className="salesButton bronze"
                    onClick={() => isEditingAllowed && setFilterList({ type: "watch" })}
                    style={{ cursor: isEditingAllowed ? "pointer" : "not-allowed" }}
                >
                    Select Products
                </div>
            </div>
            <div className="posContainer">
                {products ? (
                    products
                        .filter((product) => {
                            const allowedProducts = ["Awning", "Others", "Add On"];
                            return allowedProducts.includes(product.title);
                        })
                        .sort(awningFirst)
                        .map((product, i) => (
                            <div
                                key={i}
                                className="posItem"
                                onClick={() => {
                                    if (isEditingAllowed) {
                                        setColor(product.color || []);
                                        setProduct(product);
                                        setItemDetails({
                                            name: `${product.title}`,
                                        });
                                        window.scrollTo(0, 0);
                                        setColorPicker(true);
                                        if (isMobile) setSelectProductsModalOpen(false);
                                    }
                                }}
                                style={{ cursor: isEditingAllowed ? "pointer" : "not-allowed" }}
                            >
                                <div className="productDetails">{product.title}</div>
                            </div>
                        ))
                ) : (
                    <></>
                )}
            </div>
        </>
    );

    const totalExpenses = expenses ? Object.values(expenses).reduce((sum, value) => sum + (parseFloat(value) || 0), 0) : 0;
    const orderTotal = (parseFloat(total) || 0) + totalExpenses;
    return (
        <div className="p-6 products-tab-page">
            <div className="pos">
                <div className="userContainer">
                    <div
                        className="checkoutButtons"
                        style={{ cursor: 'pointer' }}
                        onClick={() => setProductInfoOpen(!productInfoOpen)}
                    >
                        <div
                            className="totalButton"
                            style={{
                                padding: '12px 20px',
                                background: '#1f2937',
                                color: '#ffffff',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                            }}
                        >
                            <span>PRODUCT INFORMATION</span>
                            <span style={{ fontSize: '18px' }}>{productInfoOpen ? '▼' : '▲'}</span>
                        </div>
                    </div>
                    {productInfoOpen && (
                    <>
                    <div className="w-full overflow-x-auto">
                        <table className="w-full min-w-max border-collapse">
                            <thead>
                                <tr className="bg-gradient-to-r from-orange-500 to-orange-600 text-white">
                                    <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider border-r border-orange-600">
                                        Type
                                    </th>
                                    <th className="px-3 py-3 text-center text-xs font-semibold uppercase tracking-wider border-r border-orange-600">
                                        Quantity
                                    </th>
                                    <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider border-r border-orange-600">
                                        Price
                                    </th>
                                    <th className="px-3 py-3 text-center text-xs font-semibold uppercase tracking-wider min-w-[3.5rem]">
                                        Edit
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {cart && cart.length > 0 ? (
                                    cart.map((product, i) => (
                                        <tr 
                                            key={i}
                                            className={`transition-colors duration-150 ${product.record === "late" ? "bg-yellow-200 hover:bg-yellow-300" : product.record === "normal" ? "bg-green-200 hover:bg-green-300" : "hover:bg-gray-50"}`}
                                        >
                                            {/* Type */}
                                            <td className="px-3 py-3 text-sm text-gray-700 whitespace-nowrap border-r border-gray-200">
                                                {cartLineTypeLabel(product)}
                                            </td>
                                            
                                            {/* Quantity */}
                                            <td className="px-3 py-3 border-r border-gray-200">
                                                <div className="flex items-center justify-center gap-2">
                                                    {(product.record === "normal" ? canEditQuantity : isEditingAllowed) ? (
                                                        <>
                                                            <button
                                                                onClick={() => {
                                                                    handleQuantity(
                                                                        product,
                                                                        -1,
                                                                        -product.price,
                                                                        -product.price,
                                                                        product.color,
                                                                        i
                                                                    );
                                                                }}
                                                                className="w-8 h-8 flex items-center justify-center bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors shadow-sm"
                                                            >
                                                                <Remove style={{ fontSize: "1.2rem" }} />
                                                            </button>
                                                            <span className="text-sm font-medium text-gray-900 min-w-[2rem] text-center">
                                                                {product.amount}
                                                            </span>
                                                            <button
                                                                onClick={() => {
                                                                    handleQuantity(
                                                                        product,
                                                                        1,
                                                                        product.price,
                                                                        product.price,
                                                                        product.color,
                                                                        i
                                                                    );
                                                                }}
                                                                className="w-8 h-8 flex items-center justify-center bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors shadow-sm"
                                                            >
                                                                <Add style={{ fontSize: "1.2rem" }} />
                                                            </button>
                                                        </>
                                                    ) : (
                                                        <span className="text-sm font-medium text-gray-900">{product.amount}</span>
                                                    )}
                                                </div>
                                            </td>
                                            
                                            {/* Price */}
                                            <td className="px-3 py-3 text-sm font-medium text-gray-900 whitespace-nowrap border-r border-gray-200">
                                                {process.env.REACT_APP_COUNTRY === "SINGAPORE" ? "$" : "RM"}
                                                {product.price}
                                            </td>
                                            <td className="px-2 py-3 text-center align-middle">
                                                {isEditingAllowed && typeof onEditCartProduct === "function" ? (
                                                    <button
                                                        type="button"
                                                        title="Edit product details"
                                                        aria-label="Edit product details"
                                                        onClick={() => onEditCartProduct(i)}
                                                        className="group inline-flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-b from-indigo-500 to-indigo-600 text-white shadow-sm ring-1 ring-indigo-700/20 transition-all hover:from-indigo-600 hover:to-indigo-700 hover:shadow-md hover:ring-indigo-800/30 active:scale-[0.97] focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 focus-visible:ring-offset-white"
                                                    >
                                                        <FiEdit2
                                                            className="w-[17px] h-[17px] shrink-0 drop-shadow-sm group-hover:translate-y-px"
                                                            strokeWidth={2.25}
                                                            aria-hidden
                                                        />
                                                    </button>
                                                ) : (
                                                    <span className="text-gray-300">—</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={4} className="px-3 py-8 text-center text-sm text-gray-500">
                                            No items added yet
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    <div className="summary w-full flex lightblue">
                        <div className="summarysubtotal">SubTotal</div>
                        <div className="summarysubtotal">
                            {process.env.REACT_APP_COUNTRY === "SINGAPORE"
                                ? "$"
                                : "RM"}
                            {total ? NumberFormatter.format(total) : "0"}
                        </div>
                    </div>

                    <div className="summary w-full depositPadding lightblue">
                        <div className="addDiscountItem">
                            <div className="summarysubtotal">Discount</div>
                            <input
                                name="discount"
                                type="number"
                                value={inputs.discount || ""}
                                onChange={handleChange}
                            />
                        </div>
                    </div>

                    {cashierMode === "sales" && (
                        <div className="summary totalButton lightblue">
                            <div className="summarysubtotal">Total</div>
                            <div className="summarysubtotal">
                                {process.env.REACT_APP_COUNTRY === "SINGAPORE"
                                    ? "$"
                                    : "RM"}
                                {inputs.discount
                                    ? NumberFormatter.format(
                                          orderTotal - inputs.discount
                                      )
                                    : NumberFormatter.format(orderTotal)}
                            </div>
                        </div>
                    )}
                    </>
                    )}

                    {cashierMode === "sales" && !hideExpenses && (
                        <>
                            {/* Expenses Section */}
                            <div 
                                className="checkoutButtons" 
                                style={{ cursor: 'pointer' }}
                                onClick={() => setExpensesOpen(!expensesOpen)}
                            >
                                <div className="totalButton" style={{ padding: '12px 20px', background: '#1f2937', color: '#ffffff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span>EXPENSES</span>
                                    <span style={{ fontSize: '18px' }}>{expensesOpen ? '▼' : '▲'}</span>
                                </div>
                            </div>

                            {expensesOpen && (
                                <>
                                <div className="possummaryContainer lightblue">
                                    <div className="formGridContainer">
                                        <div className="addProductItem">
                                            <label>Commission</label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                value={expenses?.commission || 0}
                                                onChange={(e) => handleExpensesChange('commission', e.target.value)}
                                                placeholder="0.00"
                                            />
                                        </div>
                                        <div className="addProductItem">
                                            <label>3D Drawing</label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                value={expenses?.threedDrawing || 0}
                                                onChange={(e) => handleExpensesChange('threedDrawing', e.target.value)}
                                                placeholder="0.00"
                                            />
                                        </div>
                                        <div className="addProductItem">
                                            <label>Crane</label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                value={expenses?.crane || 0}
                                                onChange={(e) => handleExpensesChange('crane', e.target.value)}
                                                placeholder="0.00"
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="summary totalButton lightblue">
                                    <div className="summarysubtotal">Total Expenses</div>
                                    <div className="summarysubtotal">
                                        {process.env.REACT_APP_COUNTRY === "SINGAPORE" ? "$" : "RM"}
                                        {NumberFormatter.format(totalExpenses)}
                                    </div>
                                </div>
                                </>
                            )}
                        </>
                    )}

                    <div
                        className="checkoutButtons"
                        style={{ cursor: 'pointer' }}
                        onClick={() => setCustomerDetailsOpen(!customerDetailsOpen)}
                    >
                        <div className="totalButton" style={{ padding: '12px 20px', background: '#1f2937', color: '#ffffff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span>CUSTOMER DETAILS</span>
                            <span style={{ fontSize: '18px' }}>{customerDetailsOpen ? '▼' : '▲'}</span>
                        </div>
                    </div>
                    {customerDetailsOpen && (
                    <div className="possummaryContainer lightblue">
                        <div className="formGridContainer">
                            {/* Row 1 */}
                            <div className="addProductItem">
                                <label>CO Number</label>
                                <input
                                    type="text"
                                    name="coNumber"
                                    placeholder="CO Number"
                                    data-no-capitalize
                                    value={inputs.coNumber || ""}
                                    onChange={handleChange}
                                />
                            </div>
                            <div className="addProductItem">
                                <label>Payment Type</label>
                                <Select
                                    {...commonSelectProps}
                                    options={PAYMENT_TYPE_OPTIONS}
                                    value={
                                        PAYMENT_TYPE_OPTIONS.find(
                                            (opt) => opt.value === inputs.paymentType
                                        ) || null
                                    }
                                    onChange={(selected) =>
                                        handleChange({
                                            target: {
                                                name: "paymentType",
                                                value: selected ? selected.value : "",
                                            },
                                        })
                                    }
                                    menuPortalTarget={document.body}
                                    placeholder="Select Payment Type"
                                />
                            </div>
                            <div className="addProductItem">
                                <label>First Payment</label>
                                <input
                                    type="number"
                                    name="firstPayment"
                                    placeholder="First Payment Amount"
                                    value={inputs.firstPayment || ""}
                                    onChange={handleChange}
                                />
                            </div>
                            {ORDER_FORM_PAYMENTS.map((field) => (
                                <div className="addProductItem" key={field.name}>
                                    <label>{field.label}</label>
                                    <input
                                        type="number"
                                        name={field.name}
                                        placeholder={field.placeholder}
                                        value={inputs[field.name] ?? ""}
                                        onChange={handleChange}
                                    />
                                </div>
                            ))}
                            {(superadmin || manager || outright) && useTotalCostBreakdown && (
                                <>
                                    {TOTAL_COST_BREAKDOWN_FIELDS.map(({ name, label }) => (
                                        <div className="addProductItem" key={name}>
                                            <label>{label}</label>
                                            <input
                                                type="number"
                                                name={name}
                                                placeholder={`${label} Amount`}
                                                value={inputs[name] ?? ""}
                                                onChange={handleChange}
                                            />
                                        </div>
                                    ))}
                                    <div className="addProductItem">
                                        <label>Total Cost</label>
                                        <input
                                            type="number"
                                            name="totalCost"
                                            placeholder="Total Cost Amount"
                                            value={getTotalCostBreakdownSum(inputs) ?? ""}
                                            readOnly
                                            disabled
                                        />
                                    </div>
                                </>
                            )}
                            {(superadmin || manager || outright) && !useTotalCostBreakdown && (
                                <div className="addProductItem">
                                    <label>Total Cost</label>
                                    <input
                                        type="number"
                                        name="totalCost"
                                        placeholder="Total Cost Amount"
                                        value={inputs.totalCost ?? ""}
                                        onChange={handleChange}
                                    />
                                </div>
                            )}

                            <div
                                className="addProductItem fullWidth"
                                style={{
                                    gridColumn: "1 / -1",
                                    border: "1px solid #bfdbfe",
                                    borderRadius: "10px",
                                    padding: "14px 16px",
                                    background: "linear-gradient(180deg, #f8fbff 0%, #eff6ff 100%)",
                                    marginTop: "4px",
                                }}
                            >
                                <div
                                    style={{
                                        fontSize: "13px",
                                        fontWeight: 700,
                                        color: "#1e3a8a",
                                        marginBottom: "12px",
                                        letterSpacing: "0.02em",
                                    }}
                                >
                                    Service / installation address
                                </div>
                                <div
                                    className="formGridContainer"
                                    style={{
                                        display: "grid",
                                        gridTemplateColumns: "repeat(2, 1fr)",
                                        gap: "12px",
                                    }}
                                >
                                    <div className="addProductItem">
                                        <label>Address Line 1</label>
                                        <input
                                            type="text"
                                            name="lineOne"
                                            placeholder="Address Line 1"
                                            value={inputs.lineOne || ""}
                                            onChange={handleChange}
                                        />
                                    </div>
                                    <div className="addProductItem">
                                        <label>Address Line 2</label>
                                        <input
                                            type="text"
                                            name="lineTwo"
                                            placeholder="Address Line 2"
                                            value={inputs.lineTwo || ""}
                                            onChange={handleChange}
                                        />
                                    </div>
                                    <div className="addProductItem">
                                        <label>Postal Code</label>
                                        <input
                                            type="text"
                                            name="zip"
                                            placeholder="Postal Code"
                                            value={inputs.zip || ""}
                                            onChange={handleChange}
                                        />
                                    </div>
                                    <div className="addProductItem">
                                        <label>City</label>
                                        <input
                                            type="text"
                                            name="city"
                                            placeholder="City"
                                            value={inputs.city || ""}
                                            onChange={handleChange}
                                        />
                                    </div>
                                    <div className="addProductItem">
                                        <label>State</label>
                                        <input
                                            type="text"
                                            name="country"
                                            placeholder="State"
                                            value={inputs.country || ""}
                                            onChange={handleChange}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div
                                className="addProductItem fullWidth"
                                style={{
                                    gridColumn: "1 / -1",
                                    display: "flex",
                                    justifyContent: "flex-start",
                                    alignItems: "center",
                                    padding: "10px 16px 6px",
                                    width: "100%",
                                    boxSizing: "border-box",
                                }}
                            >
                                <label
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "10px",
                                        cursor: "pointer",
                                        fontSize: "13px",
                                        fontWeight: 600,
                                        color: "#4b5563",
                                        userSelect: "none",
                                        width: "100%",
                                        justifyContent: "flex-start",
                                        textAlign: "left",
                                    }}
                                >
                                    <input
                                        type="checkbox"
                                        name="billingSameAsAddress"
                                        checked={inputs.billingSameAsAddress === true}
                                        onChange={handleChange}
                                        style={{ width: "18px", height: "18px", flexShrink: 0 }}
                                    />
                                    <span>Same as service address</span>
                                </label>
                            </div>

                            {inputs.billingSameAsAddress !== true && (
                            <div
                                className="addProductItem fullWidth"
                                style={{
                                    gridColumn: "1 / -1",
                                    border: "1px solid #e9d5ff",
                                    borderRadius: "10px",
                                    padding: "14px 16px",
                                    background: "#faf5ff",
                                    marginTop: "4px",
                                }}
                            >
                                <div
                                    style={{
                                        fontSize: "13px",
                                        fontWeight: 700,
                                        color: "#4c1d95",
                                        letterSpacing: "0.02em",
                                        marginBottom: "12px",
                                    }}
                                >
                                    Billing address
                                </div>
                                <div
                                    className="formGridContainer"
                                    style={{
                                        display: "grid",
                                        gridTemplateColumns: "repeat(2, 1fr)",
                                        gap: "12px",
                                    }}
                                >
                                    <div className="addProductItem">
                                        <label>Billing Address Line 1</label>
                                        <input
                                            type="text"
                                            name="billingLineOne"
                                            placeholder="Billing Address Line 1"
                                            value={inputs.billingLineOne || ""}
                                            onChange={handleChange}
                                        />
                                    </div>
                                    <div className="addProductItem">
                                        <label>Billing Address Line 2</label>
                                        <input
                                            type="text"
                                            name="billingLineTwo"
                                            placeholder="Billing Address Line 2"
                                            value={inputs.billingLineTwo || ""}
                                            onChange={handleChange}
                                        />
                                    </div>
                                    <div className="addProductItem">
                                        <label>Billing Address Postcode</label>
                                        <input
                                            type="text"
                                            name="billingZip"
                                            placeholder="Postcode"
                                            value={inputs.billingZip || ""}
                                            onChange={handleChange}
                                        />
                                    </div>
                                    <div className="addProductItem">
                                        <label>Billing Address City</label>
                                        <input
                                            type="text"
                                            name="billingCity"
                                            placeholder="City"
                                            value={inputs.billingCity || ""}
                                            onChange={handleChange}
                                        />
                                    </div>
                                    <div className="addProductItem">
                                        <label>Billing Address State</label>
                                        <input
                                            type="text"
                                            name="billingCountry"
                                            placeholder="State"
                                            value={inputs.billingCountry || ""}
                                            onChange={handleChange}
                                        />
                                    </div>
                                </div>
                            </div>
                            )}
                            <div className="addProductItem">
                                <label>Estimated Installation Days (Optional)</label>
                                <input
                                    type="number"
                                    name="estimatedInstallationDays"
                                    placeholder="Estimated Installation Days"
                                    value={inputs.estimatedInstallationDays || ""}
                                    onChange={handleChange}
                                />
                            </div>
                            <div className="addProductItem remarkField">
                                <label>Remark (Optional)</label>
                                <textarea
                                    name="remark"
                                    placeholder="Remark"
                                    rows={3}
                                    style={{ padding: "10px", resize: "none" }}
                                    value={inputs.remark || ""}
                                    onChange={handleChange}
                                />
                            </div>
                            <div className="addProductItem">
                                <label>Leakage Warranty (Years)</label>
                                <input
                                    type="number"
                                    min="0"
                                    step="1"
                                    placeholder="e.g. 1"
                                    value={inputs.warranty?.leakageWarranty ?? ""}
                                    onChange={(e) =>
                                        handleChange({
                                            target: {
                                                name: "warranty",
                                                value: {
                                                    ...inputs.warranty,
                                                    leakageWarranty: e.target.value ? Number(e.target.value) : undefined,
                                                },
                                            },
                                        })
                                    }
                                />
                            </div>
                            <div className="addProductItem">
                                <label>ACP Warranty (Years)</label>
                                <input
                                    type="number"
                                    min="0"
                                    step="1"
                                    placeholder="e.g. 5"
                                    value={inputs.warranty?.acpWarranty ?? ""}
                                    onChange={(e) =>
                                        handleChange({
                                            target: {
                                                name: "warranty",
                                                value: {
                                                    ...inputs.warranty,
                                                    acpWarranty: e.target.value ? Number(e.target.value) : undefined,
                                                },
                                            },
                                        })
                                    }
                                />
                            </div>
                            <div className="addProductItem">
                                <label>Structure Warranty (Years)</label>
                                <input
                                    type="number"
                                    min="0"
                                    step="1"
                                    placeholder="e.g. 10"
                                    value={inputs.warranty?.structureWarranty ?? ""}
                                    onChange={(e) =>
                                        handleChange({
                                            target: {
                                                name: "warranty",
                                                value: {
                                                    ...inputs.warranty,
                                                    structureWarranty: e.target.value ? Number(e.target.value) : undefined,
                                                },
                                            },
                                        })
                                    }
                                />
                            </div>
                            <div className="addProductItem">
                                <label>Glass Warranty (Years)</label>
                                <input
                                    type="number"
                                    min="0"
                                    step="1"
                                    placeholder="e.g. 1"
                                    value={inputs.warranty?.glassWarranty ?? ""}
                                    onChange={(e) =>
                                        handleChange({
                                            target: {
                                                name: "warranty",
                                                value: {
                                                    ...inputs.warranty,
                                                    glassWarranty: e.target.value ? Number(e.target.value) : undefined,
                                                },
                                            },
                                        })
                                    }
                                />
                            </div>
                            <div className="addProductItem">
                                <label>Polycarbonate Warranty (Years)</label>
                                <input
                                    type="number"
                                    min="0"
                                    step="1"
                                    placeholder="e.g. 1"
                                    value={inputs.warranty?.polycarbonateWarranty ?? ""}
                                    onChange={(e) =>
                                        handleChange({
                                            target: {
                                                name: "warranty",
                                                value: {
                                                    ...inputs.warranty,
                                                    polycarbonateWarranty: e.target.value ? Number(e.target.value) : undefined,
                                                },
                                            },
                                        })
                                    }
                                />
                            </div>
                            <div className="addProductItem">
                                <label>Metal Deck Warranty (Years)</label>
                                <input
                                    type="number"
                                    min="0"
                                    step="1"
                                    placeholder="e.g. 1"
                                    value={inputs.warranty?.metaldeckWarranty ?? ""}
                                    onChange={(e) =>
                                        handleChange({
                                            target: {
                                                name: "warranty",
                                                value: {
                                                    ...inputs.warranty,
                                                    metaldeckWarranty: e.target.value ? Number(e.target.value) : undefined,
                                                },
                                            },
                                        })
                                    }
                                />
                            </div>
                            <div className="addProductItem cbond-warranty-field">
                                <label>Aluminium C-Bond Warranty (Years)</label>
                                <input
                                    type="number"
                                    min="0"
                                    step="1"
                                    placeholder="e.g. 5"
                                    value={inputs.warranty?.aluminiumCBondWarranty ?? ""}
                                    onChange={(e) =>
                                        handleChange({
                                            target: {
                                                name: "warranty",
                                                value: {
                                                    ...inputs.warranty,
                                                    aluminiumCBondWarranty: e.target.value ? Number(e.target.value) : undefined,
                                                },
                                            },
                                        })
                                    }
                                />
                            </div>
                            {hideExpenses ? (
                                <IncludedServicesField
                                    label={<label>Included Services</label>}
                                    value={inputs.extraWarranty}
                                    onChange={(extraWarranty) =>
                                        handleChange({
                                            target: {
                                                name: "extraWarranty",
                                                value: extraWarranty,
                                            },
                                        })
                                    }
                                />
                            ) : (
                                <div className="addProductItem remarkField">
                                    <label>Extra Warranty (Optional)</label>
                                    <textarea
                                        name="extraWarranty"
                                        placeholder="Extra Warranty Details"
                                        rows={3}
                                        style={{ padding: "10px", resize: "none" }}
                                        value={inputs.extraWarranty || ""}
                                        onChange={handleChange}
                                    />
                                </div>
                            )}
                    </div>
                </div>
                )}

                <div className="possummaryContainer lightblue" style={{ marginBottom: '15px', padding: '15px 20px' }}>
                    <div
                        onClick={() => isEditingAllowed && setSstIncluded(!sstIncluded)}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            cursor: isEditingAllowed ? 'pointer' : 'not-allowed',
                            userSelect: 'none',
                            transition: 'all 0.2s ease',
                            opacity: isEditingAllowed ? 1 : 0.6,
                        }}
                    >
                        <div style={{
                            width: '24px',
                            height: '24px',
                            border: `2px solid ${sstIncluded ? '#10b981' : '#6b7280'}`,
                            borderRadius: '6px',
                            backgroundColor: sstIncluded ? '#10b981' : '#ffffff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginRight: '12px',
                            transition: 'all 0.2s ease',
                            flexShrink: 0
                        }}>
                            {sstIncluded && (
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                    <path d="M13.5 4L6 11.5L2.5 8" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            )}
                        </div>
                        <div style={{ flex: 1 }}>
                            <div style={{
                                fontSize: '15px',
                                fontWeight: '600',
                                color: '#1f2937',
                                marginBottom: '2px'
                            }}>
                                SST Included
                            </div>
                            <div style={{
                                fontSize: '12px',
                                color: '#6b7280',
                                lineHeight: '1.4'
                            }}>
                                {sstIncluded
                                    ? 'Invoice shows U/Price, SST (5%), and Amount separately'
                                    : 'Invoice shows U/Price equal to Amount with no SST split'}
                            </div>
                        </div>
                        <div style={{
                            padding: '4px 12px',
                            borderRadius: '12px',
                            fontSize: '11px',
                            fontWeight: '600',
                            backgroundColor: sstIncluded ? '#d1fae5' : '#f3f4f6',
                            color: sstIncluded ? '#065f46' : '#6b7280',
                            transition: 'all 0.2s ease'
                        }}>
                            {sstIncluded ? 'TRUE' : 'FALSE'}
                        </div>
                    </div>
                </div>

                {orderData?.status === "draft" && (superadmin || manager || staff || outright) && (
                    <div className="possummaryContainer lightblue" style={{ marginBottom: '15px', padding: '15px 20px' }}>
                        <div 
                            onClick={() => setPendingMode(!pendingMode)}
                            style={{ 
                                display: 'flex', 
                                alignItems: 'center',
                                cursor: 'pointer',
                                userSelect: 'none',
                                transition: 'all 0.2s ease'
                            }}
                        >
                            <div style={{
                                width: '24px',
                                height: '24px',
                                border: `2px solid ${pendingMode ? '#3b82f6' : '#6b7280'}`,
                                borderRadius: '6px',
                                backgroundColor: pendingMode ? '#3b82f6' : '#ffffff',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                marginRight: '12px',
                                transition: 'all 0.2s ease',
                                flexShrink: 0
                            }}>
                                {pendingMode && (
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                        <path d="M13.5 4L6 11.5L2.5 8" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                )}
                            </div>
                            <div style={{ flex: 1 }}>
                                <div style={{ 
                                    fontSize: '15px', 
                                    fontWeight: '600',
                                    color: '#1f2937',
                                    marginBottom: '2px'
                                }}>
                                    Pending Mode
                                </div>
                                <div style={{ 
                                    fontSize: '12px', 
                                    color: '#6b7280',
                                    lineHeight: '1.4'
                                }}>
                                    {pendingMode 
                                        ? 'Order will be moved to pending status upon update' 
                                        : 'Order will remain in draft status'}
                                </div>
                            </div>
                            <div style={{
                                padding: '4px 12px',
                                borderRadius: '12px',
                                fontSize: '11px',
                                fontWeight: '600',
                                backgroundColor: pendingMode ? '#dbeafe' : '#f3f4f6',
                                color: pendingMode ? '#1e40af' : '#6b7280',
                                transition: 'all 0.2s ease'
                            }}>
                                {pendingMode ? 'ENABLED' : 'DISABLED'}
                            </div>
                        </div>
                    </div>
                )}

                {(superadmin || manager || staff || outright) && (
                    <div className="checkoutButtons">
                        <div
                            className="salesButton sales"
                            onClick={handlePosCreateOrder}
                        >
                            Update Order
                        </div>
                    </div>
                )}
                </div>

                {/* Desktop: show Select Products section inline. Mobile: show icon that opens modal */}
                <div
                    className={`posWrapper lightred purchase-order-select-materials-section ${isMobile ? "purchase-order-select-materials-hidden" : ""}`}
                    style={{ opacity: isEditingAllowed ? 1 : 0.5, pointerEvents: isEditingAllowed ? "auto" : "none" }}
                >
                    {selectProductsContent}
                </div>
                {isMobile && (
                    <>
                        <button
                            type="button"
                            className="purchase-order-select-materials-trigger"
                            onClick={() => setSelectProductsModalOpen(true)}
                            aria-label="Select products"
                        >
                            <FiShoppingCart className="purchase-order-select-materials-trigger-icon" />
                            <span>Select Products</span>
                        </button>
                        {selectProductsModalOpen && (
                            <>
                                <div
                                    className="purchase-order-select-materials-backdrop"
                                    onClick={() => setSelectProductsModalOpen(false)}
                                    aria-hidden="true"
                                />
                                <div className="purchase-order-select-materials-modal">
                                    <div className="purchase-order-select-materials-modal-header">
                                        <h3>Select Products</h3>
                                        <button
                                            type="button"
                                            className="purchase-order-select-materials-modal-close"
                                            onClick={() => setSelectProductsModalOpen(false)}
                                            aria-label="Close"
                                        >
                                            <Close />
                                        </button>
                                    </div>
                                    <div className="purchase-order-select-materials-modal-body">
                                        {selectProductsContent}
                                    </div>
                                </div>
                            </>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default ProductsTab;
